import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { logoBackPath } from "../config";
import { NameContext } from "../context/NameContext";
import { useOrbitMenuConfig } from "../hooks/useOrbitMenuConfig";
import { mainMenuItems, aboutSubmenuItems } from "../config/orbitMenuConfig";

const BitterButtonWithMenu = React.forwardRef(({
  onMenuClick,
  onOverlayOpen,
  hoverGlowInner = 3,
  hoverGlowOuter = 5,
  hoverGlowInnerAlpha = 0.46,
  hoverGlowOuterAlpha = 0.10
}, ref) => {
  const containerRef = React.useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState("main");
  const ticking = React.useRef(false);
  const [isMobileView, setIsMobileView] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { name } = useContext(NameContext);

  // Use OrbitMenu configuration
  const { config } = useOrbitMenuConfig();

  const BOTTOM_BUFFER_CLOSED = 170; // Space from bottom when menu is closed
  const BOTTOM_BUFFER_OPEN = 300; // Space from bottom when menu is open (adjust based on menu size)
  const TOP_SHIFT_ON_OPEN = 36; // Extra downward shift when menu opens near top
  const TOOLTIP_DURATION = 6000; // 6 seconds
  const SMOOTH_SCROLL = true; // ON/OFF switch for smooth scrolling

  const hoverScale = 1.1;
  const minItemGap = 8;
  const extraEdgeBuffer = 12;

  const radius = config.visual.radius;
  const buttonSize = config.visual.button.width;
  const startAngle = config.visual.startAngle || 0;

  // Build menu items with dynamic actions
  const buildMenuItems = (items) => {
    return items.map(item => {
      // Check if this item's route matches the current location
      const isActive = item.route && location.pathname === item.route;

      return {
        ...item,
        isActive,
        label: item.dynamic && item.label.includes('{name}')
          ? item.label.replace('{name}', name || '...')
          : item.label,
        onClick: () => {
          // Handle different action types
          if (item.action === 'openOverlay') {
            setIsMenuOpen(false);
            if (onOverlayOpen) onOverlayOpen();
          } else if (item.action === 'openSubmenu') {
            setCurrentMenu(item.submenu);
          } else if (item.action === 'closeSubmenu') {
            setCurrentMenu('main');
          } else if (item.route) {
            // Handle hash navigation
            if (item.route.includes('#')) {
              const [path, hash] = item.route.split('#');
              navigate(path || '/');
              const scrollToHash = () => {
                const el = document.getElementById(hash);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  setTimeout(scrollToHash, 50);
                }
              };
              setTimeout(scrollToHash, 50);
            } else {
              navigate(item.route);
            }
            setIsMenuOpen(false);
          }
        }
      };
    });
  };

  const processedMainMenu = buildMenuItems(mainMenuItems);
  const processedAboutMenu = buildMenuItems(aboutSubmenuItems);
  const currentMenuItems = currentMenu === "main" ? processedMainMenu : processedAboutMenu;

  const getPosition = (angle, effectiveRadius) => {
    const rad = ((angle + startAngle) - 90) * (Math.PI / 180);
    return {
      x: Math.cos(rad) * effectiveRadius,
      y: Math.sin(rad) * effectiveRadius
    };
  };

  const getItemAngles = (items) => {
    if (!items.length) return [];
    const start = startAngle;
    const end = (startAngle - 180);
    if (items.length === 1) return [start];
    const step = (start - end) / (items.length - 1);
    return items.map((_, index) => start - (step * index));
  };

  const getEffectiveRadius = (items) => {
    if (items.length <= 1) return radius;
    const start = startAngle;
    const end = (startAngle - 180);
    const stepDeg = Math.abs((start - end) / (items.length - 1));
    const stepRad = (stepDeg * Math.PI) / 180;
    const minChord = (buttonSize * hoverScale) + minItemGap;
    const minRadius = minChord / (2 * Math.sin(stepRad / 2));
    return Math.max(radius, minRadius);
  };

  useEffect(() => {
    // Check if tooltip was already shown (using localStorage for persistence)
    const hasSeenTooltip = localStorage.getItem('menuTooltipShown') === 'true';

    const updatePosition = () => {
      if (!containerRef.current) return;

      const currentScroll = window.scrollY;
      const viewportHeight = window.innerHeight;
      const baseTopOffset = 140; // mt-28 = 7rem = 112px
      const effectiveRadius = isMenuOpen ? getEffectiveRadius(currentMenuItems) : radius;
      const extraRadius = Math.max(0, effectiveRadius - radius);
      const topOffset = baseTopOffset + extraRadius + extraEdgeBuffer;
      const bottomBuffer = (isMenuOpen ? BOTTOM_BUFFER_OPEN : BOTTOM_BUFFER_CLOSED) + extraRadius + extraEdgeBuffer;

      // Calculate maximum translateY - ensures button never goes below virtual bottom
      const maxTranslateY = Math.max(0, viewportHeight - topOffset - bottomBuffer);

      // Clamp translateY to valid range
      const clampedTranslateY = Math.min(Math.max(0, currentScroll), maxTranslateY);

      // When the menu opens near the top, nudge the button down a bit for space
      const nearTopThreshold = 2;
      const shouldShiftDown = isMenuOpen && clampedTranslateY <= nearTopThreshold;
      const desiredTranslateY = clampedTranslateY + (shouldShiftDown ? TOP_SHIFT_ON_OPEN : 0);
      const finalTranslateY = Math.min(desiredTranslateY, maxTranslateY);

      // Apply transform - single positioning mode
      containerRef.current.style.transform = `translateY(${finalTranslateY}px)`;
      containerRef.current.style.top = `${topOffset}px`;
      containerRef.current.style.bottom = 'auto';

      // Show tooltip after user scrolls a bit (100px), only once ever, and only when menu is closed
      if (currentScroll > 100 && !hasScrolled && !hasSeenTooltip && !isMenuOpen) {
        setHasScrolled(true);
        setShowTooltip(true);

        // Mark as shown in localStorage
        localStorage.setItem('menuTooltipShown', 'true');

        // Hide after TOOLTIP_DURATION
        setTimeout(() => {
          setShowTooltip(false);
        }, TOOLTIP_DURATION);
      }

      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(updatePosition);
        ticking.current = true;
      }
    };

    // Initial position calculation on mount and when menu state changes
    updatePosition();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasScrolled, isMenuOpen, currentMenuItems, radius, buttonSize]);

  useEffect(() => {
    if (!isMenuOpen) {
      setCurrentMenu("main");
    }
    document.dispatchEvent(new CustomEvent("orbit-menu-change", { detail: { isOpen: isMenuOpen } }));
  }, [isMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (event) => setIsMobileView(event.matches);
    setIsMobileView(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const handleClick = () => {
    setIsMenuOpen((prev) => !prev);
  };

  // Extract color and style config
  const buttonBgColor = config.visual.colors?.buttonBg || '#1b1b1b';
  const buttonOutlineColor = config.visual.colors?.buttonOutline || '#AC8E66';
  const buttonOutlineWidth = config.visual.colors?.buttonOutlineWidth || 1;
  const menuItemBgColor = config.visual.colors?.menuItemBg || '#1a1a1a';
  const menuItemOutlineColor = config.visual.colors?.menuItemOutline || '#f97316';
  const menuItemOutlineWidth = config.visual.colors?.menuItemOutlineWidth || 1;
  const menuItemTextColor = config.visual.colors?.menuItemText || '#d0cbb8';
  const menuItemFontSize = config.visual.button?.fontSize || '9px';
  const logoType = config.visual.logo?.type || 'image';
  const logoText = config.visual.logo?.text || 'B';
  const logoImage = config.visual.logo?.image || logoBackPath;
  const usesDefaultLogoImage = !config.visual.logo?.image;
  const isImageLogo = logoType === 'image';
  const imagePadding = isImageLogo ? 0 : 4;
  const innerSize = Math.max(0, buttonSize - (imagePadding * 2));
  const mobileButtonBg = config.visual.colors?.mobileButtonBg || '#1a1a1a';
  const mobileButtonOutlineColor = config.visual.colors?.mobileButtonOutline || buttonOutlineColor;
  const mobileButtonOutlineWidth = config.visual.colors?.mobileButtonOutlineWidth ?? buttonOutlineWidth;
  const effectiveBorderWidth = isMobileView ? mobileButtonOutlineWidth : buttonOutlineWidth;
  const effectiveBorderColor = isMobileView ? mobileButtonOutlineColor : buttonOutlineColor;
  const computedBorder = effectiveBorderWidth > 0 ? `${effectiveBorderWidth}px solid ${effectiveBorderColor}` : "none";
  const effectiveButtonBg = isMobileView ? mobileButtonBg : buttonBgColor;
  const computedBg = isImageLogo && !isMobileView ? 'transparent' : effectiveButtonBg;

  return (
    <>
      <motion.div
        ref={(node) => {
          containerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className="fixed right-4 z-[100]"
        data-bitter-button-menu="true"
      >
        {/* Tooltip */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{
            opacity: showTooltip ? 1 : 0,
            x: showTooltip ? 0 : 20
          }}
          transition={{ duration: 0.3 }}
          className="absolute right-16 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ display: showTooltip ? 'block' : 'none' }}
        >
          <div className=" bg-[#1a1a1a] text-xs font-mono px-3 py-2 rounded-lg shadow-lg whitespace-nowrap relative" style={{ color: '#AC8E66' }}>
            Menü öffnen
            <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-gray-900"></div>
          </div>
        </motion.div>

        <motion.div
          className="relative flex items-center justify-center cursor-pointer rounded-full"
          style={{
            width: `${buttonSize}px`,
            height: `${buttonSize}px`,
            backgroundColor: computedBg,
            border: computedBorder,
            boxShadow: 'none'
          }}
          whileHover={{
            scale: 1.1,
            boxShadow: isImageLogo
              ? `0 0 ${hoverGlowInner}px rgba(215,172,116,${hoverGlowInnerAlpha}), 0 0 ${hoverGlowOuter}px rgba(215,172,116,${hoverGlowOuterAlpha})`
              : `0 0 ${Math.max(12, hoverGlowInner - 4)}px rgba(215,172,116,${Math.max(0.12, hoverGlowInnerAlpha - 0.06)}), 0 0 ${Math.max(28, hoverGlowOuter - 12)}px rgba(215,172,116,${Math.max(0.1, hoverGlowOuterAlpha - 0.06)})`
          }}
          onClick={handleClick}
        >
          {logoType === 'image' ? (
            <div
              className="flex items-center justify-center rounded-full overflow-hidden"
              style={{ width: `${innerSize}px`, height: `${innerSize}px` }}
            >
              <img
                src={logoImage}
                srcSet={usesDefaultLogoImage ? "/images/responsive/deni_round-128.webp 128w, /images/responsive/deni_round-256.webp 256w, /images/responsive/deni_round-384.webp 384w" : undefined}
                sizes={usesDefaultLogoImage ? `${Math.ceil(innerSize)}px` : undefined}
                alt="Denis Bitter - Button"
                className="w-full h-full rounded-full object-cover"
                width={Math.ceil(innerSize)}
                height={Math.ceil(innerSize)}
                loading="lazy"
                decoding="async"
              />
            </div>
          ) : (
            <span className="text-slate-50 font-bold text-xl">
              {logoText}
            </span>
          )}
        </motion.div>

        {/* Radial Menu Items - attached to button */}
        <AnimatePresence>
          {isMenuOpen && (
            <div className="absolute top-0 left-0">
              {(() => {
                const angles = getItemAngles(currentMenuItems);
                const effectiveRadius = getEffectiveRadius(currentMenuItems);
                return currentMenuItems.map((item, index) => {
                  const position = getPosition(angles[index], effectiveRadius);

                  return (
                    <motion.button
                      key={`${currentMenu}-${item.label}`}
                      initial={{
                        scale: 0,
                        x: 0,
                        y: 0,
                        opacity: 0
                      }}
                      animate={{
                        scale: 1,
                        x: position.x,
                        y: position.y,
                        opacity: 1
                      }}
                      exit={{
                        scale: 0,
                        x: 0,
                        y: 0,
                        opacity: 0
                      }}
                      transition={{
                        type: "spring",
                        stiffness: config.animation.menuItem.stiffness,
                        damping: config.animation.menuItem.damping,
                        delay: index * config.animation.menuItem.staggerDelay
                      }}
                      onClick={item.onClick}
                      whileHover={{ scale: 1.12, border: 'solid 2px #AC8E66' }}
                      whileTap={{ scale: 0.55, blur: '10px' }}
                      className="absolute rounded-full flex flex-col items-center justify-center cursor-pointer shadow-lg transform transition-colors"
                      style={{
                        width: `${buttonSize}px`,
                        height: `${buttonSize}px`,
                        backgroundColor: menuItemBgColor,
                        color: menuItemTextColor,
                        border: `${menuItemOutlineWidth}px dotted ${menuItemOutlineColor}`,
                        fontSize: menuItemFontSize
                      }}
                    >
                      <span className="font-mono text-center leading-tight px-1 ">
                        {item.label}
                      </span>
                    </motion.button>
                  );
                });
              })()}
            </div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Backdrop when menu is open */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: config.animation.backdrop.duration }}
            className="fixed inset-0 z-[99]"
            style={{
              backgroundColor: config.visual.colors.backdrop,
              backdropFilter: `blur(${config.visual.backdrop.blur})`,
              WebkitBackdropFilter: `blur(${config.visual.backdrop.blur})`
            }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
});

BitterButtonWithMenu.displayName = 'BitterButtonWithMenu';

export default BitterButtonWithMenu;
